import React from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Heading,
  Text,
  Skeleton,
  useColorModeValue,
  Button,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'

import { DataRecord, MyRecord } from '../types'
import { formatDate } from '../utils/helpers'
import CollapsibleList from './CollapsibleList'

type RecordRow = DataRecord | MyRecord

interface DataTableProps {
  data: RecordRow[]
  isLoading: boolean
  error: unknown
  showUserId?: boolean
  recordType?: 'Data' | 'Alpha'
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading,
  error,
  showUserId = false,
  recordType = 'Data',
}) => {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const tableBg = useColorModeValue('white', 'gray.800')

  const handleRowClick = (id: number) => {
    navigate(`/record/data/${id}`)
  }

  const isMyRecord = (record: RecordRow): record is MyRecord => {
    return 'symbols' in record || 'target_symbols' in record || 'features_original' in record
  }

  const getSymbols = (record: RecordRow): string[] => {
    if (isMyRecord(record)) {
      if (record.symbols && record.symbols.length > 0) {
        return record.symbols
      }

      return (record.target_symbols || [])
        .map((item) => item.symbol)
        .filter((symbol): symbol is string => Boolean(symbol))
    }

    if (record.symbol_data && Object.keys(record.symbol_data).length > 0) {
      return Object.keys(record.symbol_data)
    }

    return record.target_asset ? [record.target_asset] : []
  }

  const getFeatures = (record: RecordRow): string[] => {
    if (isMyRecord(record)) {
      const features = record.features_original || record.features_sorted || []
      return features.map((feature) => feature.name)
    }

    if (record.symbol_data) {
      const firstSymbol = Object.values(record.symbol_data)[0]
      if (firstSymbol?.features) {
        return firstSymbol.features.map((feature) => feature.name)
      }
    }

    return []
  }

  const getOwnerId = (record: RecordRow): number | null => {
    if ('user_id' in record) {
      return record.user_id
    }

    return null
  }

  const columnCount = showUserId ? 7 : 6

  const renderOwnerIdCell = (record: RecordRow) => {
    if (!showUserId) return null

    const ownerId = getOwnerId(record)
    return <Td>{ownerId ?? '-'}</Td>
  }

  const renderLoadingOwnerCell = () => {
    if (!showUserId) return null

    return (
      <Td>
        <Skeleton height="20px" />
      </Td>
    )
  }

  const renderOwnerHeader = () => {
    if (!showUserId) return null

    return <Th>Owner Id</Th>
  }

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
        <Button
          mt={6}
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer borderRadius="md" boxShadow="sm" bg={tableBg}>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Name</Th>
              {renderOwnerHeader()}
              <Th>Symbols</Th>
              <Th>Features</Th>
              <Th>Encrypted</Th>
              <Th>Last Updated</Th>
              {/* <Th>Created At</Th> */}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  {renderLoadingOwnerCell()}
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                </Tr>
              ))
            ) : data.length > 0 ? (
              data.map((record) => {
                const symbols = getSymbols(record)
                const features = getFeatures(record)

                return (
                  <Tr
                    key={record.id}
                    _hover={{ bg: hoverBg, cursor: 'pointer' }}
                    onClick={() => handleRowClick(record.id)}
                  >
                    <Td>{record.id}</Td>
                    <Td fontWeight="medium">{record.name}</Td>
                    {renderOwnerIdCell(record)}
                    <Td>
                      <Wrap spacing={1}>
                        {symbols.map((symbol) => (
                          <WrapItem key={symbol}>
                            <Badge
                              colorScheme="blue"
                              fontSize="xs"
                              px={2}
                              py={1}
                            >
                              {symbol}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Td>
                    <Td>
                      <CollapsibleList
                        items={features?.map((f) => f) || []}
                      />
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={record.is_encrypted ? 'yellow' : 'gray'}
                        color={record.is_encrypted ? 'yellow.800' : 'gray.700'}
                        fontSize="xs"
                        p={1}
                      >
                        {record.is_encrypted ? 'ENCRYPTED' : 'Unencrypted'}
                      </Badge>
                    </Td>
                    <Td>{formatDate(record.last_updated)}</Td>
                    {/* <Td>{formatDate(record.created_at)}</Td> */}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={columnCount} textAlign="center" py={6}>
                  <Text color="gray.500">
                    No {recordType.toLowerCase()} records found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default DataTable
